import { api } from '@/api';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { readAdminStay, readAdminStays, type AdminStayFilters } from '@/api/core/stays';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { operatorConfig, operatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';

export interface StayRuntime {
  client: ServiceClient;
  session: OperatorSession;
  apiNamespace: string;
}
export const stayRuntime: StayRuntime = {
  client: api.core,
  session: operatorSession,
  apiNamespace: operatorConfig?.coreApiUrl ?? 'unconfigured',
};

export function hasStayReadAccess(session: OperatorSession, generation: number): boolean {
  const state = session.getSnapshot();
  return (
    state.generation === generation &&
    state.status === 'authorized' &&
    !!state.authorization &&
    !state.deniedDomains.includes('booking') &&
    canReadDomain(state.authorization, 'booking', 2)
  );
}

async function readCurrentStayDomain<T>(
  runtime: StayRuntime,
  generation: number,
  signal: AbortSignal,
  read: () => Promise<T>,
): Promise<T> {
  if (!hasStayReadAccess(runtime.session, generation))
    throw new Error('현재 투숙 조회 권한을 확인할 수 없습니다.');
  try {
    const result = await read();
    if (!hasStayReadAccess(runtime.session, generation))
      throw new Error('현재 투숙 조회 권한을 확인할 수 없습니다.');
    return result;
  } catch (error) {
    if (
      isApiError(error) &&
      error.status === 403 &&
      !signal.aborted &&
      hasStayReadAccess(runtime.session, generation)
    )
      runtime.session.reportDomainAccessDenied('booking', generation);
    throw error;
  }
}

export function stayListQuery(
  runtime: StayRuntime,
  generation: number,
  filters: AdminStayFilters,
  enabled: boolean,
) {
  return {
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'admin-stays'), filters],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readCurrentStayDomain(runtime, generation, signal, () =>
        readAdminStays(runtime.client, filters, signal),
      ),
    enabled: enabled && hasStayReadAccess(runtime.session, generation),
    meta: { suppressGlobalError: true },
  };
}

export function stayDetailQuery(
  runtime: StayRuntime,
  generation: number,
  stayId: string,
  enabled: boolean,
) {
  return {
    queryKey: coreQueryKey(runtime.apiNamespace, generation, 'admin-stay', stayId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readCurrentStayDomain(runtime, generation, signal, () =>
        readAdminStay(runtime.client, stayId, signal),
      ),
    enabled: enabled && hasStayReadAccess(runtime.session, generation),
    meta: { suppressGlobalError: true },
  };
}
